import type { Fraction } from '@mimir/common/util/fractionType';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { PlayableArtifact, PlayableArtifactFlat, PlayableItemDto, PlayableItemGetOptions, VirtualClipInfo } from '@mimir/common/dto/playableItemDto';
import type { SegmentProvider, SegmentInfo } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import type { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, compareFractions, divideFractions, multiplyFractions } from '@mimir/common/util/integerMath';
import { frac } from '@mimir/web/util/clientMath';
import { ItemSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { invariant } from '@mimir/common/util/invariant';
import { decodeFmp4InitSegment, demuxFmp4Segment } from '@mimir/common/mp4/fmp4Demuxer';
import type { Mp4Info } from '@mimir/common/mp4/mp4Types';
import { fetchAndInspectMoov } from '@mimir/common/mp4/mp4Demuxer';
import { buildEncodedDataFromVirtualSegment, findVirtualSegmentIndex } from '@mimir/common/mp4/virtualSegments';
import { fetchByteArrays } from '@mimir/common/mp4/fetchByteArrays';
import { Piggyback, TaskTracking } from '@mimir/common/async/taskTracking';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import type { SegmentedMediaInfo } from '@mimir/common/dto/pollingDtos';
import { detectQualityFromArtifact, getAvailableQualitiesFromItem } from '../playbackQuality';
import type { PlaybackQuality, QualityOption } from '@mimir/common/types/playbackQuality';
import type { Clock } from '@mimir/common/types/clock';
import { resilientFetch } from '@mimir/common/io/resilientFetch';
import { isRetryableSegmentResponse } from '@mimir/common/util/isRetryableSegmentResponse';

export interface MimirSegmentProviderServices {
  apiActions: VideoApiActions;
  fetch: BasicFetch;
  logger: Logger;
  itemPollingOps: ItemPollingOps;
  clock: Clock;
  demuxTs: (bytes: Uint8Array, firstSegment: boolean) => EncodedData;
}

const MAX_AUDIO_CHANNELS_IN_SINGLE_STREAM = 8;

interface PlayableArtifactResult {
  playableArtifact: PlayableArtifact | null;
  playableQuality: PlaybackQuality | null;
}

export function getPlayableArtifact(item: PlayableItemDto, requestedQuality: PlaybackQuality | null): PlayableArtifactResult {
  const playableArtifacts = item.availableArtifacts.filter(artifact => artifact.playableStatus === 'playable');
  const playableArtifact = playableArtifacts.find(entry => detectQualityFromArtifact(entry) === requestedQuality);
  if (playableArtifact) {
    return { playableArtifact, playableQuality: requestedQuality };
  }
  const firstPlayableArtifact = playableArtifacts.length > 0 ? playableArtifacts[0] : null;
  return {
    playableArtifact: firstPlayableArtifact,
    playableQuality: firstPlayableArtifact ? detectQualityFromArtifact(firstPlayableArtifact) : null,
  };
}

export function validateAudioTrackLimit(item: PlayableItemDto, playableArtifact: PlayableArtifactFlat, mp4Info: Mp4Info | undefined): boolean {
  if (mp4Info === undefined) {
    return true;
  }
  if (playableArtifact.type === 'flatEditProxy' || playableArtifact.type === 'flatHighRes') {
    return mp4Info.audioTracks.every(track => track.channelCount <= MAX_AUDIO_CHANNELS_IN_SINGLE_STREAM);
  } else if (playableArtifact.type === 'flatWebProxy') {
    return item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null;
  }
  return true;
}

async function getMp4WithAudioProxies(
  item: PlayableItemDto,
  playableArtifact: PlayableArtifactFlat,
  fetch: BasicFetch,
): Promise<Mp4Info | undefined> {
  let mp4Info: Mp4Info | undefined = undefined;
  if (playableArtifact.type === 'flatEditProxy' || playableArtifact.type === 'flatHighRes') {
    // Validate if needed before fetching audio proxies
    mp4Info = item.itemType === 'video' ? await fetchAndInspectMoov(fetch, playableArtifact.url, undefined, false, false) : undefined;
    const validAudioTrackLimit = validateAudioTrackLimit(item, playableArtifact, mp4Info);
    if (validAudioTrackLimit) {
      return mp4Info;
    }
  }
  // if the audio track limit is broken we fetch the mp4 info again without audio and add the audio proxy tracks
  mp4Info = await fetchAndInspectMoov(fetch, playableArtifact.url, undefined, false, true);
  for (const proxyAudioTrackUrl of item.proxyAudioTrackUrls) {
    mp4Info = await fetchAndInspectMoov(fetch, proxyAudioTrackUrl, mp4Info, true, false);
  }
  return mp4Info;
}

export function createMimirSegmentProvider(
  services: MimirSegmentProviderServices,
  itemId: string,
  requestedQuality: PlaybackQuality | null,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): SegmentProvider {
  return new DelegatedSegmentProvider(services, itemId, requestedQuality, ignoreVideo, ignoreAudio);
}


function createMimirSegmentProviderInternal(
  services: MimirSegmentProviderServices,
  itemId: string,
  requestedQuality: PlaybackQuality | null,
): SegmentProvider {
  const { apiActions, fetch, logger } = services;

  const segmentFetch = resilientFetch(
    { fetch, AbortController },
    { shouldRetry: isRetryableSegmentResponse, retryDelayLimit: 30_000 },
    services.clock,
    services.logger,
  );

  const options : PlayableItemGetOptions = {
    useEditProxy: true,
    streamSetPreference: [
      [StreamRoleId.LiveRecordWebproxyTs],
      [StreamRoleId.LiveRecordAudioOnlyProxyTs],
      [StreamRoleId.LiveRecordAudioOnlyHighresTs],
      [StreamRoleId.LiveRecordHighresTs],
      [StreamRoleId.EditProxyFull],
    ],
    peakOffset: undefined,
  };

  let closed = false;
  let cachedItem : PlayableItemDto | null = null;
  let itemSegmentCache: ItemSegmentUrlCache | undefined;
  let format: 'ts' | 'fmp4' | 'flatEditProxy' | 'flatWebProxy' | 'flatHighRes' | undefined;
  let mp4Info: Mp4Info | undefined;

  let currentQuality: PlaybackQuality | null = null;

  const onDurationChangedListeners : ((duration: Fraction) => void)[] = [];

  let segmentedMedia: Partial<Record<StreamRoleId, SegmentedMediaInfo>> = {};
  let lastDuration: Fraction | null = null;

  const pollListenerId = services.itemPollingOps.addPollListener(itemId, ['playableDuration', 'segmentedMedia'], response => {
    if (response === null) {
      return;
    }

    segmentedMedia = response.segmentedMedia;

    if (response.playableDuration !== null) {
      const playableDuration = response.playableDuration;
      if (lastDuration === null || compareFractions(lastDuration, playableDuration) !== 0) {
        lastDuration = playableDuration;
        onDurationChangedListeners.forEach((l) => l(playableDuration));
      }
    }

  });

  const getItem = async (refresh: boolean): Promise<PlayableItemDto> => {
    const initialFetch = (cachedItem === null);
    if (initialFetch || refresh) {
      cachedItem = await apiActions.getPlayableItem(itemId, options);
      if (cachedItem === null) {
        throw Error('Failed to get playable item');
      }

      return cachedItem;
    }
    if (cachedItem === null) {
      throw Error('Failed to get playable item');
    }
    return cachedItem;
  };

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const item = await getItem(false);
      const { playableArtifact, playableQuality } = getPlayableArtifact(item, requestedQuality);
      if (!playableArtifact || !playableQuality) {
        throw Error('No playable artifact found for item');
      }

      if (playableArtifact.type === 'flatHighRes' || playableArtifact.type === 'flatEditProxy' || playableArtifact.type === 'flatWebProxy') {
        // If we have a web proxy for a stereo input that was created without any downmixing,
        // we can use it as a single flat file, so we treat it as an edit proxy
        const webProxyWithStereoInput = playableArtifact.type === 'flatWebProxy' && item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null;
        format = webProxyWithStereoInput ? 'flatEditProxy' : playableArtifact.type;

        if (mp4Info === undefined) {
          const audioTracksBelowLimit = (playableArtifact.type === 'flatEditProxy' || playableArtifact.type === 'flatHighRes') && item.proxyAudioTrackUrls.length <= MAX_AUDIO_CHANNELS_IN_SINGLE_STREAM;
          const audioProxiesNotNeeded = webProxyWithStereoInput || audioTracksBelowLimit;
          if (audioProxiesNotNeeded) {
            mp4Info = await fetchAndInspectMoov(fetch, playableArtifact.url, undefined, false, false);
          } else {
            mp4Info = await getMp4WithAudioProxies(item, playableArtifact, fetch);
          }
        }

        if (mp4Info === undefined) {
          throw Error('Item has no media');
        }
        currentQuality = playableQuality;
        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.media === null) {
        throw Error('Item has no media');
      }

      if (playableArtifact.type !== 'stream' || playableArtifact.streamSet.length === 0) {
        throw new Error('Item has unsupported media');
      }

      format = playableArtifact.streamSet[0] === StreamRoleId.EditProxyFull ? 'fmp4' : 'ts';
      itemSegmentCache = new ItemSegmentUrlCache(
        apiActions,
        item.id,
        'get',
        playableArtifact.streamSet[0],
        10,
      );
      const segmentDuration = item.media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      const segmentCount = segmentedMedia[playableArtifact.streamSet[0]]?.segmentCount ?? item.media.segmentCount;
      if (segmentIndex < 0 || segmentIndex >= segmentCount) {
        return null;
      }

      const segmentIndexAsFraction = frac(segmentIndex, 1);

      currentQuality = playableQuality;

      return {
        id: (segmentIndex + (format === 'fmp4' ? 1 : 0)).toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }

      if (format === 'flatEditProxy' || format === 'flatWebProxy' || format === 'flatHighRes') {
        invariant(mp4Info !== undefined, 'Should have fetched mp4 info');
        const virtualSegment = mp4Info.virtualSegments[segmentIndex];
        const byteArrays = await fetchByteArrays(fetch, virtualSegment);
        return buildEncodedDataFromVirtualSegment(byteArrays, virtualSegment, mp4Info);
      }

      invariant(itemSegmentCache !== undefined && format !== undefined, () => `Should have invoked getSegmentInfoAt() because asking for segment ${id}`);
      if (format === 'fmp4' && mp4Info === undefined) {
        const url = await itemSegmentCache.getMediaSegmentUrl(0);
        logger.info('MimirSegmentProvider: fetching init fmp4 segment');
        const response = await fetch(url, { });

        if (!response.ok || (response.body === null)) {
          throw new Error(`Failed to fetch init segment: ${response.status} ${response.statusText}`);
        }
        const body = response.body;
        const fmp4InitSegmentBytes = await readWholeStream(body);
        mp4Info = decodeFmp4InitSegment(fmp4InitSegmentBytes, false, false);
      }

      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await segmentFetch(url, { });

      if (!response.ok || response.body === null) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const bytes = await readWholeStream(response.body);
      return mp4Info === undefined
        ? services.demuxTs(bytes, false)
        : demuxFmp4Segment(logger, mp4Info, bytes);
    },
    async getDuration(): Promise<Fraction> {
      const item = await getItem(true);
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      if (!item.playableDuration) {
        throw Error('Item does not have duration');
      }
      return item.playableDuration;
    },
    async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
      const item = await getItem(false); // getDuration() always runs first and does getItem(true), populating the cache
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      return item.virtualClipInfo;
    },
    async getCurrentQuality(): Promise<PlaybackQuality | null> {
      if (currentQuality !== null) {
        return currentQuality;
      }
      const item = await getItem(false);
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      const { playableQuality } = getPlayableArtifact(item, requestedQuality);
      currentQuality = playableQuality;
      return playableQuality;
    },
    async getAvailableQualities(): Promise<QualityOption[]> {
      const item = await getItem(false);
      return getAvailableQualitiesFromItem(item, null);
    },
    close(): void {
      if (closed) {
        throw new Error('MimirSegmentProvider already closed');
      }
      closed = true;
      services.itemPollingOps.removePollListener(pollListenerId);
    },
    addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
      onDurationChangedListeners.push(onDurationChanged);
      return () => {
        const index = onDurationChangedListeners.indexOf(onDurationChanged);
        if (index !== -1) {
          onDurationChangedListeners.splice(index, 1);
        }
      };
    }
  };
}

class DelegatedSegmentProvider implements SegmentProvider {

  static segmentProviders: Record<string, CachedSegmentProvider> = {};

  private cached: CachedSegmentProvider;
  private cacheKey: string;
  private durationListeners: ((duration: Fraction) => void)[] = [];
  private innerDurationListenerRemoval: () => void;

  constructor(
    services: MimirSegmentProviderServices,
    itemId: string,
    requestedQuality: PlaybackQuality | null,
    private ignoreVideo: boolean,
    private ignoreAudio: boolean,
  ) {
    this.cacheKey = requestedQuality !== null ? `${itemId}:${requestedQuality}` : itemId;
    let cached: CachedSegmentProvider | undefined = DelegatedSegmentProvider.segmentProviders[this.cacheKey];
    if (!cached) {
      cached = {
        nReferences: 0,
        innerProvider: createMimirSegmentProviderInternal(services, itemId, requestedQuality),
        taskTrackingGetSegmentInfoAt: new TaskTracking(),
        taskTrackingGetSegment: new TaskTracking(),
        piggyBackGetDuration: new Piggyback(),
        piggyBackGetVirtualClipInfo: new Piggyback(),
        piggyBackGetCurrentQuality: new Piggyback(),
        piggyBackGetAvailableQualities: new Piggyback(),
      };
      DelegatedSegmentProvider.segmentProviders[this.cacheKey] = cached;
    }
    this.cached = cached;
    this.cached.nReferences++;
    this.innerDurationListenerRemoval = this.cached.innerProvider.addDurationChangedListener(duration => this.durationListeners.forEach(l => l(duration)));
  }

  async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
    const id = `${position.numerator}:${position.denominator}`;
    const info = await this.cached.taskTrackingGetSegmentInfoAt.runOrPiggyback(id, () => this.cached.innerProvider.getSegmentInfoAt(position));
    return info;
  }

  async getSegment(id: string): Promise<EncodedData> {
    const data = await this.cached.taskTrackingGetSegment.runOrPiggyback(id, () => this.cached.innerProvider.getSegment(id));

    return {
      encodedVideoData: this.ignoreVideo ? undefined : data.encodedVideoData,
      encodedAudioData: this.ignoreAudio ? [] : data.encodedAudioData,
    };
  }

  async getDuration(): Promise<Fraction> {
    return await this.cached.piggyBackGetDuration.runOrPiggyback(() => this.cached.innerProvider.getDuration());
  }

  async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
    return await this.cached.piggyBackGetVirtualClipInfo.runOrPiggyback(() => this.cached.innerProvider.getVirtualClipInfo());
  }

  async getCurrentQuality(): Promise<PlaybackQuality | null> {
    return await this.cached.piggyBackGetCurrentQuality.runOrPiggyback(() => this.cached.innerProvider.getCurrentQuality());
  }

  async getAvailableQualities(): Promise<QualityOption[]> {
    return await this.cached.piggyBackGetAvailableQualities.runOrPiggyback(() => this.cached.innerProvider.getAvailableQualities());
  }

  addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
    this.durationListeners.push(onDurationChanged);
    return () => {
      const index = this.durationListeners.indexOf(onDurationChanged);
      if (index !== -1) {
        this.durationListeners.splice(index, 1);
      }
    };
  }

  close(): void {
    this.cached.nReferences--;
    this.innerDurationListenerRemoval();
    this.durationListeners.splice(0);

    // When dropping an item onto the timeline, a source is created
    // as part of the ongoing drag operation. When the operation is completed,
    // the timeline model converts the drag operation into an undo stack operation
    // by undoing the temporary operation (which would remove the source) and
    // executing the operation that is pushed on the undo stack (which would re-add
    // the source). Since the completion of a drag operation is a synchronous
    // operation, the remove source and add source events are going to be triggered
    // within the same event loop iteration. As a consequence, using a setTimeout(0)
    // to defer the actual destruction of the inner provider to the next tick allows
    // the second add source event to re-use the existing provider instead of destroying
    // it and re-creating a new one immediately
    setTimeout(() => {
      // Since several sources can reuse the same provider, they can trigger
      // simultaneous cleanup if they are all removed as part of the same timeline
      // operation. So let's check DelegatedSegmentProvider.segmentProviders directly
      // instead of looking at this.cached in order to avoid double-free problems in
      // case another close() call has already triggered the cleanup
      const cleanupCandidate = DelegatedSegmentProvider.segmentProviders[this.cacheKey];
      if (cleanupCandidate !== undefined && cleanupCandidate.nReferences === 0) {
        cleanupCandidate.innerProvider.close();
        delete DelegatedSegmentProvider.segmentProviders[this.cacheKey];
      }
    }, 0);
  }

}


interface CachedSegmentProvider {
  innerProvider: SegmentProvider;
  nReferences: number;
  taskTrackingGetSegmentInfoAt: TaskTracking<SegmentInfo | null>;
  taskTrackingGetSegment: TaskTracking<EncodedData>;
  piggyBackGetDuration: Piggyback<Fraction>;
  piggyBackGetVirtualClipInfo: Piggyback<VirtualClipInfo | null>;
  piggyBackGetCurrentQuality: Piggyback<PlaybackQuality | null>;
  piggyBackGetAvailableQualities: Piggyback<QualityOption[]>;
}
